import React from 'react';

const HermesAgent = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Hermes Agent is an open-source, long-lived AI agent runtime from Nous Research — the
      same team behind the Hermes model family. Instead of resetting every chat session, it is
      built to keep durable memory, run scheduled work across messaging channels, and
      optionally improve over time by turning finished tasks into reusable &quot;skills&quot;
      in a closed learning loop.
    </p>

    <h2>Why &quot;persistent&quot; agents matter</h2>
    <p>
      Most assistants forget everything when the tab closes. For research, ops, and coding
      workflows, that forces users to re-explain context and repeat tool setup. Hermes targets
      teams who want something closer to a process that runs for days — with memory you
      control, automation hooks, and guardrails around shell and browser actions.
    </p>

    <h2>What the stack emphasizes</h2>
    <ul>
      <li>
        <strong>Pluggable memory:</strong> Designs around searchable storage (e.g. SQLite FTS)
        plus summarization so the agent can recall prior work without stuffing the whole
        history into the prompt every time.
      </li>
      <li>
        <strong>Reflective skill growth:</strong> After tasks, a reflection phase can extract
        patterns and write skill files the agent can load later — reducing repeated reasoning
        for similar jobs. Self-learning features are typically opt-in and off by default.
      </li>
      <li>
        <strong>Operational posture:</strong> Compared with some ecosystem-first agent kits,
        Hermes leans conservative: container-oriented deployment, namespaced subagents, and
        pre-execution scanning for risky terminal commands.
      </li>
      <li>
        <strong>Channels:</strong> Integrations span common chat surfaces (e.g. Telegram,
        Discord, Slack) so the same runtime can notify you or accept tasks outside a IDE.
      </li>
    </ul>

    <h2>Migration and competition</h2>
    <p>
      Nous documents migration paths from other open agent stacks so teams can move configs,
      memories, and skills without a full rewrite. Analysts and early adopters often contrast
      Hermes with broader marketplaces: Hermes optimizes for internal procedural growth and
      memory, while some alternatives prioritize plug-in volume and community extensions.
    </p>

    <div className="news-insight-box">
      <h3>The big lesson</h3>
      <p>
        The next wave of &quot;agent&quot; products is not only smarter models — it is
        durable state, scheduling, and governance. Hermes is a bet that open-source runtimes
        with explicit memory and safety boundaries will matter as much as raw benchmark
        scores for real automation on developer and operations machines.
      </p>
    </div>
  </article>
);

export default HermesAgent;
